import { useCallback, useEffect, useState } from 'react';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from './authErrors';
import { ApiError } from '../types/api';
import { useAuth } from './auth';

/**
 * The request fields for a step-up: a 6-digit authenticator code goes as
 * `totpCode`, anything else (a recovery code) as `recoveryCode`. Spaces
 * are dropped, so "123 456" works.
 */
export function stepUpBody(input: string): { totpCode: string } | { recoveryCode: string } {
  const code = input.replace(/\s/g, '');
  return /^\d{6}$/.test(code) ? { totpCode: code } : { recoveryCode: code };
}

export function stepUpCodeReady(input: string): boolean {
  return input.replace(/\s/g, '').length >= 6;
}

export const STEP_UP_ENDED_MESSAGE =
  'Your 30-minute window has ended. Enter a code from the authenticator app.';

/** True for the 401 the API sends when no code came and the window is closed. */
export function isStepUpRequired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401 && err.body?.error === 'step_up_required';
}

/**
 * The 30-minute step-up window ("sudo mode"). While `active`, the forms send no code.
 * Re-evaluated every 30 s so the code field returns when the window runs out.
 * `onStepUpSuccess` (after any step-up action that sent a code) and `onStepUpError`
 * (on a failure) re-read /auth/me, so the next form matches the server's window.
 */
export function useStepUpWindow(): {
  active: boolean;
  minutesLeft: number;
  onStepUpSuccess: () => void;
  onStepUpError: (err: unknown) => void;
} {
  const { user, refreshMe } = useAuth();
  const until = user?.stepUpUntil ?? null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (until === null) return;
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [until]);
  const active = until !== null && until > now;
  const minutesLeft = active ? Math.max(1, Math.ceil((until - now) / 60_000)) : 0;
  const onStepUpSuccess = useCallback(() => {
    void refreshMe();
  }, [refreshMe]);
  const onStepUpError = useCallback(
    (err: unknown) => {
      if (isStepUpRequired(err)) void refreshMe();
    },
    [refreshMe],
  );
  return { active, minutesLeft, onStepUpSuccess, onStepUpError };
}

/** The message for a failed step-up (401 wrong/used code, 429 locked), or null for any other error. */
export function stepUpErrorText(err: unknown): string | null {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (err instanceof ApiError && err.status === 401) {
    if (err.body?.error === 'step_up_required') return STEP_UP_ENDED_MESSAGE;
    return 'The code is incorrect or already used.';
  }
  return null;
}
